import React, { useCallback, useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import {
  ArrowLeft, Mail, Building2, Phone, Loader2, Sparkles, MapPin, Briefcase, Users2,
  LinkIcon, Send, StickyNote, User as UserIcon, Info, Globe, Tag, FileText, Clock,
  CalendarClock, Handshake, Layers, Settings2, MessageSquare, Pencil, X, Check,
} from 'lucide-react';
import AdminHeader from './AdminHeader';
import { useAdminAuth } from '../context/AdminAuthContext';
import { getPartner, updatePartner, addPartnerNote, enrichPartner, bookingConfig, sendPartnerBookingLink } from '../lib/marketingApi';
import { formatApiError } from '../lib/auth';
import { PARTNER_STATUSES, partnerStatusMeta, INTEREST_LABEL } from './PartnersCRM';

const ROLE_OPTS = ['product_manager', 'operations_manager', 'business_owner', 'energy_broker', 'account_manager', 'project_manager', 'other'];

const ROLE_LABEL = {
  product_manager: 'Product Manager', operations_manager: 'Operations Manager', business_owner: 'Business Owner',
  energy_broker: 'Energy Broker', account_manager: 'Account Manager', project_manager: 'Project Manager', other: 'Other',
};
const TABS = [
  { key: 'application', label: 'Application', icon: FileText },
  { key: 'activity', label: 'Activity', icon: MessageSquare },
  { key: 'company', label: 'Company Research', icon: Building2 },
];
const CONF_CLS = { high: 'bg-emerald-100 text-emerald-700', medium: 'bg-amber-100 text-amber-700', low: 'bg-slate-100 text-slate-600' };
const arr = (v) => (Array.isArray(v) ? v : []);
const val = (v) => (v === null || v === undefined || v === '' ? '—' : v);
function fmt(iso) {
  if (!iso) return '';
  try { return new Date(iso).toLocaleString('en-US', { dateStyle: 'medium', timeStyle: 'short' }); } catch (e) { return iso; }
}

function Card({ className = '', children, ...rest }) {
  return <div className={`rounded-lg border border-slate-200 bg-white ${className}`} {...rest}>{children}</div>;
}
function CardTitle({ icon: Icon, children, right }) {
  return (
    <div className="flex items-center justify-between border-b border-slate-100 px-5 py-3">
      <div className="flex items-center gap-2 text-sm font-semibold text-slate-800">{Icon && <Icon className="h-4 w-4 text-[#0035A8]" />} {children}</div>
      {right}
    </div>
  );
}

export default function PartnerDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAdminAuth();
  const canEdit = user?.role === 'admin' || user?.role === 'editor';

  const [p, setP] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState('application');
  const [owner, setOwner] = useState('');
  const [savingOwner, setSavingOwner] = useState(false);
  const [enriching, setEnriching] = useState(false);
  const [noteText, setNoteText] = useState('');
  const [addingNote, setAddingNote] = useState(false);
  const [teams, setTeams] = useState([]);
  const [bookTeam, setBookTeam] = useState('');
  const [sendingLink, setSendingLink] = useState(false);
  const [editing, setEditing] = useState(false);
  const [edit, setEdit] = useState({});
  const [savingEdit, setSavingEdit] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await getPartner(id);
      setP(res.partner);
      setOwner(res.partner.owner || '');
    } catch (e) {
      toast.error('Partner not found');
      navigate('/admin/partners');
    } finally {
      setLoading(false);
    }
  }, [id, navigate]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { bookingConfig().then((d) => { setTeams(d.teams || []); setBookTeam(d.default_team_id || ''); }).catch(() => {}); }, []);

  const changeStatus = async (status) => {
    try { const res = await updatePartner(id, { status }); setP(res.partner); toast.success('Status updated'); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail, 'Update failed')); }
  };
  const saveOwner = async () => {
    setSavingOwner(true);
    try { const res = await updatePartner(id, { owner }); setP(res.partner); toast.success('Owner saved'); }
    catch (e) { toast.error('Save failed'); } finally { setSavingOwner(false); }
  };
  const runEnrich = async () => {
    setEnriching(true);
    try { const res = await enrichPartner(id); setP(res.partner); toast.success('AI research generated'); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail, 'Enrichment failed')); } finally { setEnriching(false); }
  };
  const submitNote = async () => {
    if (!noteText.trim()) return;
    setAddingNote(true);
    try { const res = await addPartnerNote(id, noteText.trim()); setP(res.partner); setNoteText(''); }
    catch (e) { toast.error('Could not add note'); } finally { setAddingNote(false); }
  };
  const sendLink = async () => {
    setSendingLink(true);
    try { const res = await sendPartnerBookingLink(id, bookTeam ? { team_id: bookTeam } : {}); setP(res.partner); toast.success(`Booking link sent via ${res.team || 'default team'}`); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail, 'Could not send booking link')); } finally { setSendingLink(false); }
  };

  const startEdit = () => {
    setEdit({
      name: p.name || '', email: p.email || '', company: p.company || '', phone: p.phone || '',
      website: p.website || '', role: p.role || '', employee_count: p.employee_count != null ? String(p.employee_count) : '', message: p.message || '',
    });
    setEditing(true);
  };
  const saveEdit = async () => {
    setSavingEdit(true);
    try { const res = await updatePartner(id, edit); setP(res.partner); setEditing(false); toast.success('Partner details updated'); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail, 'Update failed')); } finally { setSavingEdit(false); }
  };

  if (loading || !p) {
    return (
      <div className="min-h-screen bg-slate-100">
        <AdminHeader active="partners" />
        <div className="flex items-center justify-center py-32 text-slate-400"><Loader2 className="h-6 w-6 animate-spin" /></div>
      </div>
    );
  }

  const enr = p.enrichment || {};
  const company = enr.company || {};
  const notes = arr(p.notes_log);
  const sm = partnerStatusMeta(p.status);
  const initials = (p.name || p.email || '?').trim().charAt(0).toUpperCase();

  return (
    <div className="min-h-screen bg-slate-100" data-testid="partner-detail">
      <AdminHeader active="partners" />
      <div className="mx-auto max-w-7xl px-5 py-6 sm:px-8">
        <button onClick={() => navigate('/admin/partners')} data-testid="partner-back" className="mb-4 inline-flex items-center gap-1.5 text-sm font-medium text-slate-500 transition-colors hover:text-slate-800">
          <ArrowLeft className="h-4 w-4" /> All partners
        </button>

        <div className="grid grid-cols-1 gap-6 lg:grid-cols-12">
          {/* MAIN */}
          <div className="space-y-5 lg:col-span-8">
            <Card className="p-6">
              <div className="flex flex-wrap items-start justify-between gap-4">
                <div className="flex min-w-0 items-start gap-4">
                  <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-lg bg-[#0035A8] text-lg font-bold text-white">{initials}</span>
                  <div className="min-w-0">
                    <h1 className="text-xl font-bold tracking-tight text-slate-900">{p.name}</h1>
                    <div className="mt-2 flex flex-col gap-1 text-sm text-slate-600">
                      <a href={`mailto:${p.email}`} className="inline-flex items-center gap-2 hover:text-[#0035A8]"><Mail className="h-4 w-4 text-slate-400" /> {p.email}</a>
                      {p.company && <span className="inline-flex items-center gap-2"><Building2 className="h-4 w-4 text-slate-400" /> {p.company}</span>}
                      {p.website && <a href={p.website.startsWith('http') ? p.website : `https://${p.website}`} target="_blank" rel="noreferrer" className="inline-flex items-center gap-2 hover:text-[#0035A8]"><Globe className="h-4 w-4 text-slate-400" /> {p.website}</a>}
                      {p.phone && <a href={`tel:${p.phone}`} className="inline-flex items-center gap-2 hover:text-[#0035A8]"><Phone className="h-4 w-4 text-slate-400" /> {p.phone}</a>}
                    </div>
                  </div>
                </div>
                <div className="flex flex-col items-end gap-2">
                  <span className={`inline-flex rounded-md px-2.5 py-1 text-xs font-semibold ${sm.cls}`}>{sm.label}</span>
                  <span data-testid="partner-owner-badge" className="inline-flex items-center gap-1 rounded-md bg-slate-100 px-2.5 py-1 text-xs font-medium text-slate-600">
                    <UserIcon className="h-3 w-3" /> {p.owner ? `Owner: ${p.owner}` : 'Unassigned'}
                  </span>
                  <div className="text-right text-xs text-slate-400"><span className="inline-flex items-center gap-1"><Clock className="h-3 w-3" /> {fmt(p.created_at)}</span></div>
                </div>
              </div>

              <div className="mt-5 border-t border-slate-100 pt-4">
                <div className="mb-2 text-xs font-semibold uppercase tracking-wide text-slate-500">Move to stage</div>
                <div className="flex flex-wrap gap-1.5" data-testid="partner-status">
                  {PARTNER_STATUSES.map((s) => (
                    <button key={s.value} disabled={!canEdit} onClick={() => changeStatus(s.value)}
                      className={`rounded-md px-2.5 py-1 text-xs font-medium transition-all disabled:cursor-not-allowed disabled:opacity-60 ${p.status === s.value ? s.cls + ' ring-2 ring-[#0035A8] ring-offset-1' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'}`}>
                      {s.label}
                    </button>
                  ))}
                </div>
              </div>
            </Card>

            <Card>
              <div className="flex gap-1 border-b border-slate-200 px-3" data-testid="partner-tabs">
                {TABS.map((t) => (
                  <button key={t.key} onClick={() => setTab(t.key)} data-testid={`partner-tab-${t.key}`}
                    className={`relative inline-flex items-center gap-1.5 px-4 py-3 text-sm font-semibold transition-colors ${tab === t.key ? 'text-[#0035A8]' : 'text-slate-500 hover:text-slate-800'}`}>
                    <t.icon className="h-4 w-4" /> {t.label}
                    {tab === t.key && <span className="absolute inset-x-2 -bottom-px h-0.5 rounded-full bg-[#0035A8]" />}
                  </button>
                ))}
              </div>

              <div className="p-5">
                {tab === 'application' && (
                  <div className="space-y-4" data-testid="tab-application">
                    <div className="flex items-center justify-between">
                      <div className="mb-0 flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide text-slate-500"><Layers className="h-3.5 w-3.5" /> Interested in</div>
                      {canEdit && !editing && (
                        <button onClick={startEdit} data-testid="partner-edit-btn" className="inline-flex items-center gap-1.5 rounded-md border border-slate-200 px-3 py-1.5 text-xs font-semibold text-slate-600 hover:border-[#0035A8] hover:text-[#0035A8]"><Pencil className="h-3.5 w-3.5" /> Edit details</button>
                      )}
                    </div>
                    <div className="flex flex-wrap gap-2">
                      {arr(p.interests).length === 0 ? <span className="text-sm text-slate-400">—</span> : arr(p.interests).map((i) => (
                        <span key={i} className="rounded-md bg-brand/10 px-3 py-1 text-xs font-semibold text-brand">{INTEREST_LABEL[i] || i}</span>
                      ))}
                    </div>

                    {editing ? (
                      <div className="space-y-3 border-t border-slate-100 pt-4" data-testid="partner-edit-form">
                        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                          <PEdit label="Name" value={edit.name} onChange={(v) => setEdit({ ...edit, name: v })} testid="partner-edit-name" />
                          <PEdit label="Email" value={edit.email} onChange={(v) => setEdit({ ...edit, email: v })} testid="partner-edit-email" />
                          <PEdit label="Company" value={edit.company} onChange={(v) => setEdit({ ...edit, company: v })} testid="partner-edit-company" />
                          <PEdit label="Phone" value={edit.phone} onChange={(v) => setEdit({ ...edit, phone: v })} testid="partner-edit-phone" />
                          <PEdit label="Website" value={edit.website} onChange={(v) => setEdit({ ...edit, website: v })} testid="partner-edit-website" />
                          <PEdit label="Number of employees" value={edit.employee_count} onChange={(v) => setEdit({ ...edit, employee_count: v })} testid="partner-edit-employees" />
                          <label className="block">
                            <span className="mb-1 block text-[11px] font-semibold uppercase tracking-wide text-slate-500">Role</span>
                            <select value={edit.role} onChange={(e) => setEdit({ ...edit, role: e.target.value })} data-testid="partner-edit-role" className="h-9 w-full rounded-md border border-slate-300 px-2.5 text-sm outline-none focus:border-[#0035A8]">
                              <option value="">—</option>
                              {ROLE_OPTS.map((r) => <option key={r} value={r}>{ROLE_LABEL[r] || r}</option>)}
                            </select>
                          </label>
                        </div>
                        <label className="block">
                          <span className="mb-1 block text-[11px] font-semibold uppercase tracking-wide text-slate-500">Message</span>
                          <textarea value={edit.message} onChange={(e) => setEdit({ ...edit, message: e.target.value })} rows={4} data-testid="partner-edit-message" className="w-full resize-y rounded-md border border-slate-300 px-3 py-2 text-sm outline-none focus:border-[#0035A8]" />
                        </label>
                        <div className="flex justify-end gap-2">
                          <button onClick={() => setEditing(false)} data-testid="partner-edit-cancel" className="inline-flex h-9 items-center gap-1.5 rounded-md border border-slate-200 px-3.5 text-sm font-semibold text-slate-600 hover:bg-slate-50"><X className="h-4 w-4" /> Cancel</button>
                          <button onClick={saveEdit} disabled={savingEdit} data-testid="partner-edit-save" className="inline-flex h-9 items-center gap-1.5 rounded-md bg-[#0035A8] px-4 text-sm font-semibold text-white hover:bg-[#002B87] disabled:opacity-60">{savingEdit ? <Loader2 className="h-4 w-4 animate-spin" /> : <Check className="h-4 w-4" />} Save changes</button>
                        </div>
                      </div>
                    ) : (
                      <>
                        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                          <Field label="Role" value={val(ROLE_LABEL[p.role] || p.role)} />
                          <Field label="Number of employees" value={val(p.employee_count)} />
                          <Field label="Website" value={val(p.website)} />
                          <Field label="Phone" value={val(p.phone)} />
                        </div>
                        {p.message ? (
                          <div className="rounded-lg border border-slate-100 bg-slate-50 p-4">
                            <div className="mb-1 flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide text-slate-500"><FileText className="h-3.5 w-3.5" /> Message</div>
                            <p className="whitespace-pre-wrap text-sm leading-relaxed text-slate-700">{p.message}</p>
                          </div>
                        ) : (
                          <div className="rounded-lg border border-dashed border-slate-200 py-8 text-center text-sm text-slate-400">No message submitted.</div>
                        )}
                      </>
                    )}
                  </div>
                )}

                {tab === 'activity' && (
                  <div data-testid="tab-activity">
                    {canEdit && (
                      <div className="mb-4 rounded-lg border border-slate-200 bg-slate-50 p-3">
                        <textarea value={noteText} onChange={(e) => setNoteText(e.target.value)} rows={3} data-testid="partner-note-input" placeholder="Log a call, a discussion or the next action…" className="w-full resize-none rounded-md border border-slate-200 px-3 py-2 text-sm outline-none focus:border-[#0035A8]" />
                        <div className="mt-2 flex justify-end">
                          <button onClick={submitNote} disabled={addingNote || !noteText.trim()} data-testid="partner-note-submit" className="inline-flex h-9 items-center gap-1.5 rounded-md bg-[#0035A8] px-4 text-sm font-semibold text-white hover:bg-[#002B87] disabled:opacity-60"><Send className="h-4 w-4" /> Add to activity</button>
                        </div>
                      </div>
                    )}
                    {notes.length === 0 ? (
                      <div className="flex flex-col items-center gap-2 py-12 text-slate-400"><StickyNote className="h-8 w-8" /><p className="text-sm">No activity yet. Discussions, call notes and stage changes will appear here.</p></div>
                    ) : (
                      <div className="space-y-3" data-testid="partner-activity-list">
                        {[...notes].reverse().map((n) => (
                          <div key={n.id} className={`rounded-lg border p-4 ${n.kind === 'system' ? 'border-slate-100 bg-slate-50' : 'border-slate-100 bg-white'}`}>
                            <div className="mb-1 flex items-center justify-between text-xs text-slate-400">
                              <span className="inline-flex items-center gap-1.5 font-medium text-slate-600">
                                {n.kind === 'system' ? <Settings2 className="h-3.5 w-3.5 text-slate-400" /> : <MessageSquare className="h-3.5 w-3.5 text-[#0035A8]" />}
                                {n.author}
                              </span>
                              <span>{fmt(n.created_at)}</span>
                            </div>
                            <p className={`whitespace-pre-wrap text-sm ${n.kind === 'system' ? 'italic text-slate-500' : 'text-slate-700'}`}>{n.text}</p>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}

                {tab === 'company' && (
                  <div data-testid="tab-company" className="space-y-4">
                    {!enr.company ? (
                      <div className="flex flex-col items-center gap-3 rounded-lg border border-dashed border-slate-200 py-12 text-center">
                        <Sparkles className="h-8 w-8 text-slate-300" />
                        <p className="max-w-sm text-sm text-slate-500">No company research yet. Use AI Research in the right panel to generate a best-effort profile (incl. estimated company size) from the application details.</p>
                        {enriching && <Loader2 className="h-5 w-5 animate-spin text-[#0035A8]" />}
                      </div>
                    ) : (
                      <>
                        {company.confidence && <span className={`inline-flex items-center gap-1 rounded-md px-2.5 py-1 text-xs font-semibold ${CONF_CLS[company.confidence] || CONF_CLS.low}`}>Confidence: {company.confidence}</span>}
                        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                          <IconField icon={Building2} label="Company" value={val(company.name)} />
                          <IconField icon={Briefcase} label="Industry" value={val(company.industry)} />
                          <IconField icon={Users2} label="Estimated size" value={val(company.size)} />
                          <IconField icon={MapPin} label="HQ" value={val(company.hq)} />
                          <IconField icon={LinkIcon} label="Website" value={val(company.website)} />
                          <IconField icon={Info} label="Founded" value={val(company.founded)} />
                        </div>
                        {company.description && <SummaryBox label="Description" text={company.description} />}
                        {arr(company.products).length > 0 && (
                          <div>
                            <div className="mb-1.5 flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide text-slate-500"><Sparkles className="h-3.5 w-3.5" /> Products</div>
                            <div className="flex flex-wrap gap-2">{arr(company.products).map((it, i) => <span key={i} className="rounded-md bg-slate-100 px-3 py-1 text-xs text-slate-700">{it}</span>)}</div>
                          </div>
                        )}
                        {enr.generated_at && <div className="pt-1 text-[11px] text-slate-400">Generated {fmt(enr.generated_at)}</div>}
                      </>
                    )}
                  </div>
                )}
              </div>
            </Card>
          </div>

          {/* SIDEBAR */}
          <aside className="space-y-5 lg:col-span-4">
            <Card>
              <CardTitle icon={UserIcon}>Assigned owner</CardTitle>
              <div className="p-5">
                <p className="mb-2 text-xs text-slate-500">Who is chasing this partner up?</p>
                <div className="flex gap-2">
                  <input value={owner} onChange={(e) => setOwner(e.target.value)} disabled={!canEdit} data-testid="partner-owner" placeholder="Assign owner…" className="h-10 flex-1 rounded-md border border-slate-200 px-3 text-sm outline-none focus:border-[#0035A8] disabled:bg-slate-50" />
                  {canEdit && <button onClick={saveOwner} disabled={savingOwner} data-testid="partner-owner-save" className="rounded-md bg-slate-900 px-4 text-sm font-medium text-white hover:bg-slate-700 disabled:opacity-60">{savingOwner ? '…' : 'Save'}</button>}
                </div>
              </div>
            </Card>

            <Card>
              <CardTitle icon={Sparkles}>AI Research</CardTitle>
              <div className="space-y-3 p-5">
                <div className="flex items-start gap-2 rounded-md bg-amber-50 px-3 py-2 text-xs leading-relaxed text-amber-700">
                  <Info className="mt-0.5 h-3.5 w-3.5 shrink-0" /> {enr.disclaimer || 'AI-generated best-effort estimate (incl. company size). Verify before use.'}
                </div>
                {canEdit ? (
                  <button onClick={runEnrich} disabled={enriching} data-testid="partner-enrich-btn" className="inline-flex h-10 w-full items-center justify-center gap-1.5 rounded-md bg-[#0035A8] text-sm font-semibold text-white hover:bg-[#002B87] disabled:opacity-60">
                    {enriching ? <Loader2 className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4" />}
                    {enr.company ? 'Regenerate research' : 'Generate with AI'}
                  </button>
                ) : <p className="text-xs text-slate-400">Read-only — ask an editor to generate research.</p>}
                {enr.generated_at && <div className="text-center text-[11px] text-slate-400">Last run {fmt(enr.generated_at)}</div>}
              </div>
            </Card>

            <Card>
              <CardTitle icon={CalendarClock}>Book a call</CardTitle>
              <div className="space-y-3 p-5">
                {p.booking_link_sent_at && <div className="rounded-md bg-emerald-50 px-3 py-2 text-xs text-emerald-700">Booking link last sent {fmt(p.booking_link_sent_at)}</div>}
                {teams.length === 0 ? (
                  <p className="text-xs text-slate-500">No scheduler configured yet. Add one in <span className="font-semibold">Settings → Booking &amp; Scheduling</span>.</p>
                ) : (
                  <>
                    <select value={bookTeam} onChange={(e) => setBookTeam(e.target.value)} data-testid="partner-book-team" className="h-10 w-full rounded-md border border-slate-200 px-3 text-sm outline-none focus:border-[#0035A8]">
                      {teams.map((t) => <option key={t.id} value={t.id}>{t.name}{t.is_default ? ' (default)' : ''}</option>)}
                    </select>
                    {canEdit ? (
                      <button onClick={sendLink} disabled={sendingLink} data-testid="partner-send-booking-link" className="inline-flex h-10 w-full items-center justify-center gap-1.5 rounded-md bg-[#0035A8] text-sm font-semibold text-white hover:bg-[#002B87] disabled:opacity-60">
                        {sendingLink ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />} Send booking link
                      </button>
                    ) : <p className="text-xs text-slate-400">Read-only — ask an editor to send a link.</p>}
                  </>
                )}
              </div>
            </Card>

            <Card>
              <CardTitle icon={Tag}>Source &amp; tracking</CardTitle>
              <dl className="divide-y divide-slate-100">
                <MetaRow icon={Handshake} label="Source" value={val(p.source)} />
                <MetaRow icon={FileText} label="Page" value={val(p.page)} />
                <MetaRow icon={Globe} label="Language" value={(p.language || 'en').toUpperCase()} />
                <MetaRow icon={Sparkles} label="UTM campaign" value={val(p.utm_campaign)} />
              </dl>
            </Card>
          </aside>
        </div>
      </div>
    </div>
  );
}

function Field({ label, value }) {
  return (
    <div className="rounded-lg border border-slate-100 p-3">
      <div className="text-xs font-semibold uppercase tracking-wide text-slate-500">{label}</div>
      <div className="mt-0.5 text-sm font-medium text-slate-800">{value}</div>
    </div>
  );
}
function PEdit({ label, value, onChange, testid }) {
  return (
    <label className="block">
      <span className="mb-1 block text-[11px] font-semibold uppercase tracking-wide text-slate-500">{label}</span>
      <input value={value} onChange={(e) => onChange(e.target.value)} data-testid={testid} className="h-9 w-full rounded-md border border-slate-300 px-3 text-sm outline-none focus:border-[#0035A8]" />
    </label>
  );
}
function IconField({ icon: Icon, label, value }) {
  return (
    <div className="flex items-start gap-2.5 rounded-lg border border-slate-100 p-3">
      <Icon className="mt-0.5 h-4 w-4 shrink-0 text-slate-400" />
      <div className="min-w-0">
        <div className="text-[11px] font-semibold uppercase tracking-wide text-slate-400">{label}</div>
        <div className="truncate text-sm font-medium text-slate-800">{value}</div>
      </div>
    </div>
  );
}
function MetaRow({ icon: Icon, label, value }) {
  return (
    <div className="flex items-center justify-between gap-3 px-5 py-2.5">
      <span className="inline-flex items-center gap-2 text-sm text-slate-500"><Icon className="h-3.5 w-3.5 text-slate-400" /> {label}</span>
      <span className="truncate text-sm font-medium text-slate-800">{value}</span>
    </div>
  );
}
function SummaryBox({ label = 'Summary', text }) {
  return (
    <div className="rounded-lg border border-slate-100 bg-slate-50 p-4">
      <div className="text-xs font-semibold uppercase tracking-wide text-slate-500">{label}</div>
      <p className="mt-1 whitespace-pre-wrap text-sm leading-relaxed text-slate-700">{text}</p>
    </div>
  );
}
